import { useEffect, useState } from "react";
import { logout, setOutlookIntegration } from "../../redux/slices/authSlice";
import { clearSentryUser } from "../../observability/sentry/sentry.identity";
import { customToast } from "../../utils/custom-toast/custom-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  useChangePasswordMutation,
  useLogoutMutation,
  useConnectOutlookMutation,
  useDisconnectOutlookMutation,
} from "../../redux/services/authApi";
import { UserData } from "../../utils/global-types/global-types";
import { RootState } from "../../redux/store";
import ProfileView from "./ProfileView";
import EditProfile from "./EditProfile";
import ChangePassword from "./ChangePassword";

export type OutlookConnectStatus = "idle" | "connecting" | "failed";

const ProfileCard = ({
  userData,
  setOpenProfileCard,
  openProfileCard,
  setOpenEmailSignatureModal,
  setOpenAgentAvailabilityLeavesModal,
}: {
  userData: UserData;
  setOpenProfileCard: (show: boolean) => void;
  openProfileCard: boolean;
  setOpenEmailSignatureModal: (show: boolean) => void;
  setOpenAgentAvailabilityLeavesModal: (show: boolean) => void;
}) => {
  const dispatch = useDispatch();
  const outlookIntegration = useSelector(
    (state: RootState) => state?.auth?.outlookIntegration
  );
  const [logoutApi] = useLogoutMutation();
  const [changePassword] = useChangePasswordMutation();
  const [connectOutlookApi] = useConnectOutlookMutation();
  const [disconnectOutlookApi, { isLoading: isDisconnecting }] =
    useDisconnectOutlookMutation();
  const [outlookConnectStatus, setOutlookConnectStatus] =
    useState<OutlookConnectStatus>("idle");
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showScreen, setShowScreen] = useState<
    "profile" | "change-password" | "edit-profile"
  >("profile");

  const handleSignOut = async () => {
    try {
      await logoutApi({}).unwrap();
    } catch (error) {
      console.error("Logout API failed:", error);
    } finally {
      dispatch(logout());
      clearSentryUser();
    }
  };

  const handlePasswordChange = async (
    oldPassword: string,
    newPassword: string
  ) => {
    try {
      await changePassword({
        oldPassword: oldPassword,
        newPassword: newPassword,
      }).unwrap();
      customToast("Password changed successfully", "success");
      setShowScreen("profile");
    } catch (error) {
      customToast(
        (error as { data: { detail: string } })?.data?.detail ||
          "Failed to change password",
        "error"
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDisconnectModal) return;

      try {
        const target = event.target as Element;
        const isPopupContent = target?.closest?.("[data-profile-card]");
        const profileButton = target?.closest?.("[data-profile-button]");

        if (!isPopupContent && !profileButton) {
          setOpenProfileCard(false);
        }
      } catch (error) {
        console.error("Error in handleClickOutside:", error);
        setOpenProfileCard(false);
      }
    };

    if (openProfileCard) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openProfileCard, setOpenProfileCard, showDisconnectModal]);

  const handleOpenEmailSignatureModal = () => {
    setOpenEmailSignatureModal(true);
    setOpenProfileCard(false);
  };

  const handleOpenAgentAvailabilityLeavesModal = () => {
    setOpenAgentAvailabilityLeavesModal(true);
    setOpenProfileCard(false);
  };

  const handleConnectOutlook = async () => {
    if (
      outlookIntegration?.integration_status ||
      outlookConnectStatus === "connecting"
    )
      return;

    setOutlookConnectStatus("connecting");
    try {
      await connectOutlookApi({ user_email: userData?.email }).unwrap();
      dispatch(
        setOutlookIntegration({
          is_integration_available: true,
          integration_status: true,
        })
      );
      setOutlookConnectStatus("idle");
      customToast("Outlook connected successfully", "success");
    } catch {
      setOutlookConnectStatus("failed");
      customToast("Failed to connect Outlook", "error");
    }
  };

  const handleDisconnectOutlook = async () => {
    try {
      const response = await disconnectOutlookApi().unwrap();
      dispatch(
        setOutlookIntegration({
          is_integration_available: true,
          integration_status: response.integration_status,
        })
      );
      setShowDisconnectModal(false);
      setOutlookConnectStatus("idle");
      setOpenProfileCard(false);
      customToast("Outlook disconnected successfully", "success");
    } catch {
      customToast("Failed to disconnect Outlook", "error");
    }
  };

  return (
    <div
      className="absolute right-6 z-10000 top-13 w-[300px] border border-border-gray dark:border-border-gray-dk rounded-lg bg-gradient-profile-card"
      data-profile-card
    >
      {showScreen === "profile" && (
        <ProfileView
          userData={userData}
          handleSignOut={handleSignOut}
          setShowEditProfile={() => setShowScreen("edit-profile")}
          setShowChangePassword={() => setShowScreen("change-password")}
          handleOpenEmailSignatureModal={handleOpenEmailSignatureModal}
          handleOpenAgentAvailabilityLeavesModal={
            handleOpenAgentAvailabilityLeavesModal
          }
          handleConnectOutlook={handleConnectOutlook}
          outlookIntegration={outlookIntegration}
          outlookConnectStatus={outlookConnectStatus}
          showDisconnectModal={showDisconnectModal}
          setShowDisconnectModal={setShowDisconnectModal}
          handleDisconnectOutlook={handleDisconnectOutlook}
          isDisconnecting={isDisconnecting}
        />
      )}

      {showScreen === "change-password" && (
        <ChangePassword
          setShowScreen={() => setShowScreen("profile")}
          onSubmit={handlePasswordChange}
        />
      )}
      {showScreen === "edit-profile" && (
        <EditProfile
          setShowEditProfile={() => setShowScreen("profile")}
          userData={userData}
          handleConnectOutlook={handleConnectOutlook}
          outlookIntegration={outlookIntegration}
          outlookConnectStatus={outlookConnectStatus}
          showDisconnectModal={showDisconnectModal}
          setShowDisconnectModal={setShowDisconnectModal}
          handleDisconnectOutlook={handleDisconnectOutlook}
          isDisconnecting={isDisconnecting}
        />
      )}
    </div>
  );
};

export default ProfileCard;
